import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import './BarChart.css';

const BarChart = ({
  data,
  width = 400,
  height = 300,
  margin = { top: 20, right: 20, bottom: 40, left: 40 },
  xAxisLabel,
  yAxisLabel,
  className = ''
}) => {
  const svgRef = useRef();

  useEffect(() => {
    if (!data || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const xScale = d3.scaleBand()
      .domain(data.map(d => d.label))
      .range([0, innerWidth])
      .padding(0.2);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value) * 1.1])
      .range([innerHeight, 0]);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Add x-axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'middle');

    // Add y-axis
    g.append('g')
      .call(d3.axisLeft(yScale).ticks(5));

    // Add bars
    g.selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', d => xScale(d.label))
      .attr('y', d => yScale(d.value))
      .attr('width', xScale.bandwidth())
      .attr('height', d => innerHeight - yScale(d.value))
      .attr('fill', '#3498db')
      .on('mouseover', function(event, d) {
        d3.select(this).attr('fill', '#2980b9');
        // Add tooltip
        g.append('text')
          .attr('class', 'bar-tooltip')
          .attr('x', xScale(d.label) + xScale.bandwidth() / 2)
          .attr('y', yScale(d.value) - 5)
          .attr('text-anchor', 'middle')
          .text(d.value);
      })
      .on('mouseout', function() {
        d3.select(this).attr('fill', '#3498db');
        g.selectAll('.bar-tooltip').remove();
      });

    // Add x-axis label
    if (xAxisLabel) {
      g.append('text')
        .attr('class', 'axis-label')
        .attr('x', innerWidth / 2)
        .attr('y', innerHeight + margin.bottom)
        .attr('text-anchor', 'middle')
        .text(xAxisLabel);
    }

    // Add y-axis label
    if (yAxisLabel) {
      g.append('text')
        .attr('class', 'axis-label')
        .attr('transform', 'rotate(-90)')
        .attr('x', -innerHeight / 2)
        .attr('y', -margin.left + 10)
        .attr('text-anchor', 'middle')
        .text(yAxisLabel);
    }

  }, [data, width, height, margin, xAxisLabel, yAxisLabel]);

  if (!data || data.length === 0) {
    return (
      <div className={`bar-chart-empty ${className}`}>
        <i className="fas fa-chart-bar"></i>
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className={`bar-chart ${className}`}>
      <svg ref={svgRef} width={width} height={height}></svg>
    </div>
  );
};

export default BarChart;